import { useMemo, useState } from "react";

import {
  Button,
  Checkbox,
  Modal,
  type Period,
  PeriodPicker,
  SearchMultiSelect,
} from "@/components/common";
import { useCreateDispatch } from "@/hooks/useAutosped";
import { useClients } from "@/hooks/useClients";
import { extractErrorMessage } from "@/services/axiosConfig";
import { SISTEMA_OPTIONS } from "@/types/autosped";

// Default: últimos 5 anos (jun/2021 → mai/2026).
const DEFAULT_PERIOD: Period = { de: "2021-06-01", ate: "2026-05-31" };

function isoToBr(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function DispatchModal({
  workspaceId,
  onClose,
}: {
  workspaceId: number;
  onClose: () => void;
}) {
  const { data: clients = [] } = useClients(workspaceId);
  const createDispatch = useCreateDispatch(workspaceId);

  const [clientIds, setClientIds] = useState<string[]>([]);
  const [sistemas, setSistemas] = useState<string[]>([]);
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [forcar, setForcar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const empresaOptions = useMemo(
    () =>
      clients.map((c) => ({
        id: String(c.id),
        label: c.legal_name,
        sub: c.document_masked,
      })),
    [clients],
  );
  const sistemaOptions = SISTEMA_OPTIONS.map((s) => ({ id: s.value, label: s.label }));

  const submit = async () => {
    if (clientIds.length === 0 || sistemas.length === 0) {
      setError("Selecione ao menos uma empresa e um sistema SPED.");
      return;
    }
    const inicio = isoToBr(period?.de ?? null);
    const fim = isoToBr(period?.ate ?? null);
    if (!inicio || !fim) {
      setError("Informe o período (início e fim).");
      return;
    }
    setError(null);
    try {
      const res = await createDispatch.mutateAsync({
        client_ids: clientIds.map(Number),
        sistemas,
        inicio_periodo: inicio,
        fim_periodo: fim,
        forcar_redownload: forcar,
      });
      if (res.erros.length > 0) {
        window.alert(`${res.criados.length} disparo(s) criado(s), ${res.erros.length} com erro.`);
      }
      onClose();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Disparar SPED"
      subtitle="Selecione as empresas, os sistemas e o período. O download roda automático."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={createDispatch.isPending}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={createDispatch.isPending}>
            {createDispatch.isPending ? "Disparando…" : "Disparar"}
          </Button>
        </>
      }
    >
      <div className="disp-field">
        <span className="disp-label">Empresas</span>
        <SearchMultiSelect
          values={clientIds}
          onChange={setClientIds}
          options={empresaOptions}
          placeholder="Buscar empresa…"
        />
      </div>

      <div className="disp-field">
        <span className="disp-label">Sistemas SPED</span>
        <SearchMultiSelect
          values={sistemas}
          onChange={setSistemas}
          options={sistemaOptions}
          placeholder="EFD-Contribuições, ECD…"
        />
      </div>

      <div className="disp-field">
        <span className="disp-label">Período</span>
        <PeriodPicker label="Competência" value={period} onChange={setPeriod} />
      </div>

      <div className="disp-opts">
        <label className="disp-opt">
          <Checkbox checked={forcar} onChange={setForcar} />
          <span>Forçar re-download (ignora o cache)</span>
        </label>
      </div>

      {error && <p className="disp-error">{error}</p>}
    </Modal>
  );
}
